import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportBuilderService } from '../services/reportBuilder';
import { ReportSectionId } from '../components/report-builder/types';

const KEYS = {
  state: (projectId: string, sessionId?: string, scope?: string) =>
    ['report-state', projectId, sessionId, scope] as const,
  template: (projectId: string) =>
    ['report-template', projectId] as const,
};

/**
 * Main hook: fetches report state (sections + async status).
 * Polls every 5s when there's active async work (readiness running, sections generating, export running).
 */
export function useReportState(
  projectId: string,
  sessionId?: string,
  scope: 'session' | 'project' = 'project'
) {
  return useQuery({
    queryKey: KEYS.state(projectId, sessionId, scope),
    queryFn: () => reportBuilderService.getReportState(projectId, sessionId, scope),
    enabled: !!projectId,
    staleTime: 5_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const { status } = data;
      // Poll if readiness is running
      if (status.readiness === 'running') return 5_000;
      // Poll if any section is generating
      const genSections = status.generatingSections || {};
      if (Object.values(genSections).some((s: any) => s.status === 'running')) return 5_000;
      // Poll if export is running
      if (status.exportStatus === 'running') return 5_000;
      return false;
    },
  });
}

export function useReportTemplate(projectId: string) {
  return useQuery({
    queryKey: KEYS.template(projectId),
    queryFn: () => reportBuilderService.getTemplate(projectId),
    enabled: !!projectId,
  });
}

/**
 * Fire-and-forget: triggers readiness assessment.
 * Results arrive via callback → poll via useReportState.
 */
export function useAssessReadiness(
  projectId: string,
  sessionId?: string,
  scope: 'session' | 'project' = 'project'
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => reportBuilderService.assessReadiness(projectId, sessionId, scope),
    onSuccess: () => {
      // Invalidate to pick up the 'running' status immediately
      queryClient.invalidateQueries({ queryKey: KEYS.state(projectId, sessionId, scope) });
    },
  });
}

/**
 * Fire-and-forget: triggers section generation.
 * Results arrive via callback → poll via useReportState.
 */
export function useGenerateSection(
  projectId: string,
  sessionId?: string,
  scope: 'session' | 'project' = 'project'
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sectionId: ReportSectionId) =>
      reportBuilderService.generateSection(projectId, sectionId, sessionId, scope),
    onSuccess: () => {
      // Invalidate to pick up the 'running' status immediately
      queryClient.invalidateQueries({ queryKey: KEYS.state(projectId, sessionId, scope) });
    },
  });
}

export function useSaveSection(
  projectId: string,
  sessionId?: string,
  scope: 'session' | 'project' = 'project'
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sectionId, content }: { sectionId: ReportSectionId; content: string }) =>
      reportBuilderService.saveSection(projectId, sectionId, content, sessionId, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.state(projectId, sessionId, scope) });
    },
  });
}

/**
 * Fire-and-forget: triggers DOCX export.
 * Results arrive via callback → poll via useReportState.
 */
export function useExportReport(
  projectId: string,
  sessionId?: string,
  scope: 'session' | 'project' = 'project'
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientName?: string) =>
      reportBuilderService.exportDocx(projectId, clientName || undefined, sessionId, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.state(projectId, sessionId, scope) });
    },
  });
}

/**
 * Cancel a stuck readiness assessment or export.
 */
export function useCancelOperation(
  projectId: string,
  sessionId?: string,
  scope: 'session' | 'project' = 'project'
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (target: 'readiness' | 'export') =>
      reportBuilderService.cancelOperation(projectId, target, sessionId, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.state(projectId, sessionId, scope) });
    },
  });
}

export function useUploadTemplate(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => reportBuilderService.uploadTemplate(projectId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.template(projectId) });
    },
  });
}
